import { useState } from "react";
import { ChevronDown, ShoppingBag, Truck, Package, RotateCcw, HelpCircle, Building2 } from "lucide-react";
import { Link } from "react-router-dom";

const FAQ_DATA = [
  {
    category: "Ordering",
    icon: ShoppingBag,
    questions: [
      {
        q: "Can I place an order without creating an account?",
        a: "Not currently. You need to sign up to track your order, view order history, and manage your purchases. Sign-up is quick and free.",
      },
      {
        q: "What payment methods do you accept?",
        a: "We accept all major UPI apps (GPay, PhonePe, Paytm), credit/debit cards, and net banking — all via Razorpay. Cash on Delivery is not available.",
      },
      {
        q: "Can I add custom name printing to any product?",
        a: "Custom name printing is available on select products at no extra cost. You'll see the option directly on the product page if it's supported for that item.",
      },
      {
        q: "Can I edit or cancel my order after placing it?",
        a: "We process orders quickly, so cancellations or edits may not always be possible. Email support@collegedrip.in immediately after placing the order and we'll try to help if it hasn't been dispatched yet.",
      },
      {
        q: "My payment failed but money was deducted. What happens?",
        a: "This is almost always a Razorpay gateway hold and gets auto-reversed within 5–7 business days. If it doesn't resolve, email us with a screenshot and your bank reference number.",
      },
    ],
  },
  {
    category: "Shipping & Delivery",
    icon: Truck,
    questions: [
      {
        q: "How long does delivery take?",
        a: "Processing takes 1–2 business days, followed by delivery in 3–5 business days from dispatch. Total time from order to delivery is typically 4–7 business days.",
      },
      {
        q: "Do you ship outside India?",
        a: "No, we currently ship only within India. International shipping is not available at this stage.",
      },
      {
        q: "Is shipping free?",
        a: "Yes — free shipping on all orders above ₹999. A flat ₹79 shipping fee applies to orders below ₹999.",
      },
      {
        q: "How do I track my order?",
        a: "Once your order is dispatched, you'll receive a tracking link on your registered email address. You can also view your order status under My Orders after logging in.",
      },
      {
        q: "My tracking isn't updating. What do I do?",
        a: "Tracking updates can lag by up to 24 hours after dispatch. If nothing changes after 2 days, email us at support@collegedrip.in with your Order ID.",
      },
    ],
  },
  {
    category: "Products",
    icon: Package,
    questions: [
      {
        q: "How do I know which size to order?",
        a: "Each product page has a size guide. When in doubt, size up — our fits run true to standard Indian sizing. Size issues unfortunately are not covered under our return policy, so check the chart carefully.",
      },
      {
        q: "Are the college designs officially licensed by the institutions?",
        a: "No. CollegeDrip's designs are original creations inspired by campus culture and student life. They are not officially licensed or affiliated with IIT, NIT, IIM, BITS, or any other institution.",
      },
      {
        q: "What fabric and print quality can I expect?",
        a: "We use premium cotton blends and high-quality DTG (direct-to-garment) or screen printing depending on the design. Products are made to last, not just look good on arrival.",
      },
    ],
  },
  {
    category: "Returns & Refunds",
    icon: RotateCcw,
    questions: [
      {
        q: "Can I return a product I don't like or ordered the wrong size?",
        a: "We don't accept returns for change of mind or size issues. Since all products are custom-printed on demand, they can't be resold. Please use the size guide before ordering.",
      },
      {
        q: "What if my product is damaged or defective?",
        a: "If your product arrives damaged or has a manufacturing defect, we'll replace it or refund you. Email support@collegedrip.in within 48 hours of delivery with your Order ID and clear photos of the issue.",
      },
      {
        q: "How long does a refund take?",
        a: "Approved refunds are processed within 5–7 business days via Razorpay, back to your original payment method. Your bank may take an additional 1–3 days to reflect it.",
      },
    ],
  },
  {
    category: "About CollegeDrip",
    icon: Building2,
    questions: [
      {
        q: "Is CollegeDrip a registered business?",
        a: "Yes. College Drip is a registered sole proprietorship based in Bengaluru, India. GSTIN: 29CIGPN5173F1ZZ.",
      },
      {
        q: "How do I contact CollegeDrip?",
        a: "Email us at support@collegedrip.in. We respond within 24–48 hours on weekdays.",
      },
    ],
  },
];

const FAQItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        className="w-full flex justify-between items-start gap-3 py-4 text-left group"
        onClick={() => setOpen(!open)}
      >
        <span className="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors leading-relaxed">
          {question}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5 transition-transform duration-200 ${
            open ? "rotate-180 text-blue-600" : ""
          }`}
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-200 ${
          open ? "max-h-96 pb-4" : "max-h-0"
        }`}
      >
        <p className="text-sm text-gray-600 leading-relaxed">{answer}</p>
      </div>
    </div>
  );
};

const FAQPage = () => {
  const [activeCategory, setActiveCategory] = useState("All");

  const categories = ["All", ...FAQ_DATA.map((c) => c.category)];

  const filtered =
    activeCategory === "All"
      ? FAQ_DATA
      : FAQ_DATA.filter((c) => c.category === activeCategory);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Strip */}
      <div className="bg-blue-700 text-white py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <p className="text-blue-200 text-sm font-medium uppercase tracking-widest mb-2">Help Center</p>
          <h1 className="text-4xl font-extrabold mb-3">Frequently Asked Questions</h1>
          <p className="text-blue-100">
            Can't find your answer?{" "}
            <Link to="/contact" className="underline text-white font-medium hover:text-blue-200">
              Contact us directly.
            </Link>
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-10">
        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                activeCategory === cat
                  ? "bg-blue-600 text-white shadow-sm"
                  : "bg-white text-gray-600 border border-gray-200 hover:border-blue-300 hover:text-blue-600"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* FAQ Sections */}
        <div className="space-y-6">
          {filtered.map(({ category, icon: Icon, questions }) => (
            <div key={category} className="bg-white rounded-xl border border-gray-100 shadow-sm">
              {/* Category Header */}
              <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100">
                <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                  <Icon className="w-4 h-4 text-blue-600" />
                </div>
                <h2 className="font-bold text-gray-900">{category}</h2>
                <span className="ml-auto text-xs text-gray-400 font-medium">
                  {questions.length} questions
                </span>
              </div>

              {/* Questions */}
              <div className="px-6">
                {questions.map(({ q, a }) => (
                  <FAQItem key={q} question={q} answer={a} />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Still need help */}
        <div className="mt-10 bg-blue-600 rounded-xl p-8 text-center text-white">
          <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <HelpCircle className="w-6 h-6 text-white" />
          </div>
          <h3 className="text-xl font-bold mb-2">Still have questions?</h3>
          <p className="text-blue-100 text-sm mb-5">
            We respond to all queries within 24–48 hours on weekdays.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/contact"
              className="bg-white text-blue-700 font-semibold px-6 py-2.5 rounded-lg text-sm hover:bg-blue-50 transition"
            >
              Contact Support
            </Link>
            <a
              href="mailto:support@collegedrip.in"
              className="border border-blue-400 text-white font-semibold px-6 py-2.5 rounded-lg text-sm hover:bg-blue-500 transition"
            >
              support@collegedrip.in
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;